import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import DataTable from '../../../components/Table/DataTable.jsx';
import { LIST_PROVEEDORES } from './gql.js';

function formatCuit(value) {
  const digits = String(value || '')
    .replace(/[^\d]/g, '')
    .slice(0, 11);

  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2, 10)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

const columns = [
  { key: 'razonSocial', label: 'Razón social' },
  { key: 'cuit', label: 'CUIT' },
  { key: 'condicionIVA', label: 'Condición IVA' },
  { key: 'telefono', label: 'Teléfono' },
  { key: 'email', label: 'Email' },
  { key: 'activo', label: 'Estado' },
];

export default function ProveedoresList({ onCreate, onSelect, refreshKey = 0 }) {
  const [search, setSearch] = useState('');
  const [variables, setVariables] = useState({ pagination: { limit: 50, offset: 0 }, search: '' });
  const [{ data, fetching, error }, reexecute] = useQuery({ query: LIST_PROVEEDORES, variables });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setVariables((prev) => ({ ...prev, search: search.trim() || null }));
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    reexecute({ requestPolicy: 'network-only' });
  }, [refreshKey, reexecute]);

  const rows = useMemo(() => {
    if (!data?.listProveedores?.items) {
      return [];
    }
    return data.listProveedores.items.map((item) => ({
      ...item,
      cuit: formatCuit(item.cuit),
      activo: item.activo ? 'Activo' : 'Inactivo',
    }));
  }, [data]);

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Proveedores"
        subtitle="Gestioná tus proveedores y mantené la información fiscal al día."
        actions={
          <button
            type="button"
            onClick={onCreate}
            className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 font-medium text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Nuevo proveedor
          </button>
        }
      />

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <label className="mb-2 block text-sm font-medium text-[rgb(var(--fg))]" htmlFor="buscar-proveedores">
              Buscar
            </label>
            <input
              id="buscar-proveedores"
              type="search"
              placeholder="Buscar por razón social o CUIT"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.8)] px-4 py-2 text-sm text-[rgb(var(--fg))] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </div>
          <div className="col-span-12">
            <div className="rounded-xl border border-dashed border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.4)] p-4 text-sm text-muted">
              {fetching ? 'Cargando proveedores…' : error ? error.message : `${rows.length} proveedores encontrados`}
            </div>
          </div>
        </div>
        <div className="mt-6 overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.65)]">
          <DataTable
            columns={columns}
            rows={rows}
            onRowClick={(row) => onSelect?.(row.id)}
            pageSize={10}
          />
        </div>
      </section>

      <StickyActions
        left={<p className="text-sm text-muted">Seleccioná un proveedor para ver el detalle o editarlo.</p>}
        right={
          <button
            type="button"
            onClick={onCreate}
            className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Crear proveedor
          </button>
        }
      />
    </div>
  );
}
